import { useEffect, useState } from "react";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";
import { api, ApiError, type AdminStat } from "../../lib/api";
import { SubjectBadge } from "../../components/ui/Badges";
import { AdminEmptyState, AdminPageHeader } from "../../components/admin/AdminUI";
import { useAdminToken, PageLoader, PageError, todayLabel } from "./adminShared";

export function AdminAnalytics() {
  const token = useAdminToken();
  const [kpis, setKpis] = useState<AdminStat[]>([]);
  const [regChart, setRegChart] = useState<{ day: string; users: number }[]>([]);
  const [planChart, setPlanChart] = useState<{ name: string; value: number }[]>([]);
  const [resourceChart, setResourceChart] = useState<{ subject: string; count: number }[]>([]);
  const [accessReport, setAccessReport] = useState<{
    totalViews: number;
    totalDownloads: number;
    totalPractices: number;
    uniqueStudents: number;
    views7d: number;
    topResources: Array<{ id: string; title: string; resourceType: string; views: number; uniqueViewers: number }>;
  } | null>(null);
  const [reportResources, setReportResources] = useState<Awaited<ReturnType<typeof api.admin.reports>>["resources"]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) return;
    Promise.all([api.admin.analytics(token), api.admin.reports(token)])
      .then(([data, reports]) => {
        setKpis(data.kpis);
        setRegChart(data.regChart);
        setPlanChart(data.planChart);
        setResourceChart(data.resourceChart);
        setAccessReport(data.accessReport ?? reports.summary);
        setReportResources(reports.resources);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : "Failed to load analytics"))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <PageLoader />;
  if (error) return <PageError message={error} />;

  return (
    <div className="p-8 space-y-8">
      <div>
        <h1 className="text-2xl font-bold">Analytics</h1>
        <p className="text-muted-foreground text-sm">Live platform metrics</p>
      </div>
      <div className="grid grid-cols-4 gap-5">
        {kpis.map((k) => (
          <div key={k.label} className="bg-card rounded-lg p-5 border border-border relative overflow-hidden shadow-sm">
            <div className="absolute top-0 left-0 right-0 h-[3px] bg-primary" />
            <p className="text-2xl font-bold">{k.value}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{k.label}</p>
            <p className="text-xs text-success font-semibold mt-1">{k.delta}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2 bg-card rounded-lg p-6 border border-border shadow-sm">
          <h3 className="font-bold mb-4">Daily registrations (7 days)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={regChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="day" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="users" stroke="var(--primary)" strokeWidth={2.5} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-card rounded-lg p-6 border border-border shadow-sm">
          <h3 className="font-bold mb-4">Plan distribution</h3>
          {planChart.every((d) => d.value === 0) ? (
            <p className="text-sm text-muted-foreground text-center py-12">No students yet</p>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={planChart} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={4} dataKey="value">
                    <Cell fill="var(--primary)" />
                    <Cell fill="var(--secondary)" />
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex gap-4 mt-2 justify-center flex-wrap">
                {planChart.map((d, i) => (
                  <div key={d.name} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: i === 0 ? "var(--primary)" : "var(--secondary)", border: i === 1 ? "1px solid var(--border)" : undefined }} />
                    {d.name} ({d.value})
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      <div className="bg-card rounded-lg p-6 border border-border shadow-sm">
        <h3 className="font-bold mb-4">Resources by subject</h3>
        {resourceChart.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-12">No resources uploaded yet</p>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={resourceChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis dataKey="subject" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" fill="var(--primary)" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {accessReport && (
        <div className="grid grid-cols-5 gap-4">
          {[
            { label: "Total views", value: accessReport.totalViews },
            { label: "Views (7 days)", value: accessReport.views7d },
            { label: "Unique students", value: accessReport.uniqueStudents },
            { label: "Downloads", value: accessReport.totalDownloads },
            { label: "Exam practices", value: accessReport.totalPractices },
          ].map((s) => (
            <div key={s.label} className="bg-card rounded-lg p-4 border border-border shadow-sm">
              <p className="text-xl font-bold">{s.value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {reportResources.length > 0 && (
        <div className="bg-card rounded-lg border border-border overflow-hidden shadow-sm">
          <div className="px-5 py-4 border-b border-border">
            <h3 className="font-bold">Resource usage report</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Views, unique students, downloads, and exam practices per resource</p>
          </div>
          <div className="overflow-x-auto admin-scroll-x">
            <table className="w-full text-sm">
              <thead>
              <tr className="border-b border-border bg-muted/50">
                {["Title", "Subject", "Type", "Views", "Students", "Downloads", "Practices", "Last access"].map((h) => (
                  <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {reportResources.slice(0, 25).map((r) => (
                <tr key={r.id} className="hover:bg-muted/30">
                  <td className="px-4 py-2.5 font-medium max-w-[200px] truncate">{r.title}</td>
                  <td className="px-4 py-2.5"><SubjectBadge subject={r.subject} /></td>
                  <td className="px-4 py-2.5 text-muted-foreground">{r.resourceType.replace("_", " ")}</td>
                  <td className="px-4 py-2.5 font-semibold">{r.views}</td>
                  <td className="px-4 py-2.5">{r.uniqueViewers}</td>
                  <td className="px-4 py-2.5">{r.downloads}</td>
                  <td className="px-4 py-2.5">{r.practices}</td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">
                    {r.lastAccessAt ? new Date(r.lastAccessAt).toLocaleString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
